import type { ReactNode, RefObject } from "react";
import React, { useEffect } from "react";

type Ref = RefObject<HTMLElement | null> | HTMLElement | null;
export type OutsideClickRef = Ref;
export type OutsideClickRefs = Ref | Ref[];

/**
 * @param refs - The ref or refs used to determine if the click is outside.
 * @param onClick - The function to call when the click is outside of the refs.
 * @param useClick - Whether to use click or mousedown/touchstart events to detect outside clicks.
 * @param capture - Listen in the capture phase so stopPropagation on targets cannot block dismissal.
 */
export default function useOutsideClick(
  refs: OutsideClickRefs,
  onClick?: (e: React.MouseEvent) => void,
  useClick = false,
  capture = false,
) {
  useEffect(() => {
    if (!onClick) {
      return undefined;
    }
    const handleOutsideClick = (e: any) => {
      const contains = (ref: Ref, target: HTMLElement) => {
        // Disambiguate between RefObject and HTMLElement
        if (!ref) {
          return false;
        }
        const el = ref.hasOwnProperty("current") ? (ref as RefObject<HTMLElement>).current : (ref as HTMLElement);
        return !el || el.contains(target);
      };

      if (Array.isArray(refs)) {
        if (!refs.some((ref) => contains(ref, e.target))) {
          onClick(e);
        }
      } else {
        if (!contains(refs, e.target)) {
          onClick(e);
        }
      }
    };

    if (useClick) {
      document.addEventListener("click", handleOutsideClick, capture);
    } else {
      document.addEventListener("mousedown", handleOutsideClick, capture);
      document.addEventListener("touchstart", handleOutsideClick, capture);
    }

    return () => {
      if (useClick) {
        document.removeEventListener("click", handleOutsideClick, capture);
      } else {
        document.removeEventListener("mousedown", handleOutsideClick, capture);
        document.removeEventListener("touchstart", handleOutsideClick, capture);
      }
    };
  }, [refs, onClick, useClick, capture]);
}

export interface OutsideClickProps {
  onOutsideClick?: () => void;
  onInsideClick?: () => void;
  children: ReactNode;
}

export class OutsideClickHandler extends React.Component<OutsideClickProps> {
  clickCaptured = false;

  render() {
    if (typeof this.props.children === "function") {
      return (this.props.children as Function)(this.getProps());
    }

    return this.renderComponent();
  }

  renderComponent() {
    return React.createElement((this as any).props.component || "div", this.getProps(), this.props.children);
  }

  getProps() {
    return {
      onMouseDown: this.innerClick,
      onTouchStart: this.innerClick,
    };
  }

  innerClick = () => {
    this.clickCaptured = true;
    if (this.props.onInsideClick) {
      this.props.onInsideClick();
    }
  };

  componentDidMount() {
    document.addEventListener("mousedown", this.documentClick);
    document.addEventListener("touchstart", this.documentClick);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.documentClick);
    document.removeEventListener("touchstart", this.documentClick);
  }

  documentClick = () => {
    if (!this.clickCaptured) {
      if (this.props.onOutsideClick) {
        this.props.onOutsideClick();
      }
    }
    this.clickCaptured = false;
  };
}
